import {isNegativeNumber, isPositiveNumber, isZero, noop, parseInteger} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useRef} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isSchemaFieldDatabaseBacked, SchemaTag} from 'folder-items/FileConstants';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import SampleRestrictionStatusTypedValueSelector from 'inventory/sample-status/SampleRestrictionStatusTypedValueSelector';
import SampleRolesPartiesSearchInput from 'inventory/SampleRolesPartiesSearchInput';
import LongTextValueEditor from 'metadata/LongTextValueEditor';
import TextAndUrlValueEditor from 'metadata/TextAndUrlValueEditor';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import MultiSelectOptionSearchCellDropdown from 'notebook/plain-tables/selectors/MultiSelectOptionSearchCellDropdown';
import OptionCellDropdown from 'notebook/plain-tables/selectors/OptionCellDropdown';
import OptionSearchCellDropdown from 'notebook/plain-tables/selectors/OptionSearchCellDropdown';
import getFormattedCellValue from 'notebook/structured-tables/columns/FormattedCellValue';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import IllegalErrorTriangle from 'notebook/tables/errors/IllegalErrorTriangle';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import TagValueTypedValueInput, {
  INLINE_TAG_TYPES,
} from 'notebook/tables/typed-values/components/editors/TagValueTypedValueInput';
import {
  convertTagToTypedValue,
  convertTypedValueToTag,
  formatTypedValue,
  getBioentitySummaryFromTypedValue,
  getTypedValueFromBioentitySummary,
  parseTextAsFloat,
  sanitizeBenchlingBlobs,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  FloatSchemaFieldTypedValue,
  IntegerSchemaFieldTypedValue,
  StringTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import ProcedureSchemaFieldTypeSelectorForTables from 'procedures/shared-components/schema-field-type-selectors/ProcedureSchemaFieldTypeSelectorForTables';
import {ProcedureConditionReplicateCellEditor} from 'procedures/tables/ProcedureConditionReplicateColumn';
import ProcessTaskAssigneeSearchInput from 'processes/tables/ProcessTaskAssigneeSearchInput';
import ProcessTaskStatusSelector from 'processes/tables/ProcessTaskStatusSelector';
import {FIELD_TYPES, FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import BlobLinkInput from 'schemas/tags/BlobLinkInput';
import BooleanValueEditor from 'schemas/tags/BooleanValueEditor';
import ControlledFloatValueEditor from 'schemas/tags/ControlledFloatValueEditor';
import ControlledIntegerValueEditor from 'schemas/tags/ControlledIntegerValueEditor';
import LocationSelectorInput from 'schemas/tags/LocationSelectorInput';
import SelectorInput from 'schemas/tags/SelectorInput';
import {getInitialTag} from 'schemas/tags/TagUtil';
import {toProcedureSearchItemsForCell} from 'studies/search/ProcedureColumnUtils';
import ProcedureSearchInput from 'studies/search/ProcedureSearchInput';
import StudySearchInput from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {DatetimePickerContext} from 'ui-library/inputs/DatetimePicker';
import {Size} from 'ui-library/inputs/InputConstants';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {InputChangeEvent, NumericInputChangeEvent, Optional} from 'util/Types';
import uniqueId from 'util/uniqueId';

/**
 * This component provides editors for TypedValues. It doesn't have its own editing capabilities;
 * it offloads to other components, and provides simple data mapping to ensure inputs and outputs
 * are all TypedValue objects. No other supporting cell data is required, but schemas are.
 *
 * We select an editor based on the column type, not the typedValue type! This ensures that the
 * editors work properly even when the initial typedValue is empty or incorrect.
 *
 * The complex search components are provided by TagValueTypedValueInput. Simple value editors
 * are provided by TagValue editors. None of these editors expect to be rendered within a table.
 *
 * You should treat this component the same way you would a controlled input form field. It gets
 * two main props (value, onChange); all higher-level responsibilities must be provided by the
 * parent component. Things like draft state, submitting, etc, are not the purview of TypedValueEditor.
 */

export interface TypedValueEditorProps {
  // The live value being edited
  typedValue: TypedValue;

  // The evaluated cell is not necessarily the source of truth for the typedValue being edited.
  // It is needed for error statuses
  evaluatedCell?: EvaluatedCell;

  // The query string is not used by all types. The caller should track this in state,
  // knowing that the final value (once it's ready) will be a typedValue
  query?: string;

  // The "placeholder" is used differently for different input types. If we are able, we use it
  // as an input placeholder.
  placeholder?: string;

  // The label is an actual HTML label, and (when possible) is linked to the editor.
  // The "required" flag adds a design flourish only. It does no enforcement.
  label?: string;
  labelClassName?: string;
  isRequired?: boolean;

  // If this is true, no interactive control is rendered; only static values.
  locked?: boolean;

  stale?: boolean;

  // Callbacks
  onUpdateTypedValue: (typedValue: TypedValue) => void;
  onUpdateQuery: (query: string) => void;

  // Values that predate the opening of this editor
  columnConstraint: ColumnConstraint;
  rowMetadata: RowMetadata | null;
  initialTypedValue: TypedValue;
  initialQuery?: string;
  viewer?: boolean;
}

export default function TypedValueEditor({
  label,
  labelClassName,
  isRequired,
  evaluatedCell,
  stale,
  ...props
}: TypedValueEditorProps): JSX.Element {
  const editor = (
    // Need to unset DatetimePopperContainerContext here because some DatetimeInput styling are dependent on
    // the context but we dont want those styling to be applied for Mixture tables
    // TODO (BNCH-19498): remove DatetimePopperContainerContext.Provider since its a stop-gap measure and
    //  devise a better way to "unset" context
    // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
    <DatetimePickerContext.Provider value={{containerNodeSelector: undefined}}>
      <div
        className={classNames(
          css(typedValueEditorStyles.triangleWrapper),
          'notebook-export--typed-value-editor-triangle-wrapper'
        )}
        {...getTestProps({
          component: 'TypedValueEditor',
          element: 'root',
        })}
      >
        <div className="typedValueEditor">
          <TypedValueUnderlyingEditor {...props} />
        </div>
        {evaluatedCell?.error ? (
          <IllegalErrorTriangle error={evaluatedCell.error} location={TriangleLocation.FORM_FIELD} />
        ) : stale ? (
          <ErrorTriangle
            state={TriangleState.OUTDATED}
            error={{type: 'Outdated', message: 'Refresh for latest value'}}
            location={TriangleLocation.FORM_FIELD}
            dataTestAttrs={{component: 'ErrorTriangle', element: 'staleCell'}}
          />
        ) : null}
      </div>
    </DatetimePickerContext.Provider>
  );

  return label ? (
    // TODO(BNCH-15226): Label's "required" flag is not working properly. The asterisk
    //  is rendered below the form controls, rather than next to the label text.
    <Label
      required={false}
      className={classNames(labelClassName, 'u-width-full')}
      {...getTestProps({component: 'TypedValueEditor', element: 'label'})}
    >
      {label}
      <br />
      <span
        className={classNames(
          css(typedValueEditorStyles.labelDescendants),
          'notebook-export--typed-value-editor-label-descendants'
        )}
        {...getTestProps({component: 'TypedValueEditor', element: 'labelDescendants'})}
      >
        {editor}
      </span>
    </Label>
  ) : (
    editor
  );
}

// Adds a keyline (border) to an editor. Many of the underlying components that we use for rendering were
// designed to be used inside table cells, so they don't provide their own borders. Use this wrapper if
// the component needs a border.
function TypedValueEditorKeyline(props: {children: React.ReactNode; locked?: boolean}): JSX.Element {
  return (
    <div
      className={classNames(
        'editorKeylineWrapper',
        props.locked && css(typedValueEditorStyles.lockedEditorWrapper),
        props.locked && 'notebook-export--typed-value-editor-locked-editor-wrapper'
      )}
      {...getTestProps({component: 'TypedValueEditor', element: 'keylineWrapper'})}
    >
      {props.children}
    </div>
  );
}

// Legacy (pre-ARRAY) multi-select values are stored as a single string, with individual
// selections joined by ", ". Splitting on every comma is unsafe because an option's own name
// may contain a comma. Only split when doing so recovers a set of segments that are all
// recognized options; otherwise, treat the whole string as one (possibly comma-containing)
// selection, since we can't otherwise tell where one option ends and the next begins.
function splitLegacyJoinedSelectorOptions(value: string, options: Array<string | OptionData>): Array<string> {
  const trimmedValue = value.trim();
  if (trimmedValue === '') {
    return [];
  }

  const knownOptionValues = new Set(
    options.map((option) => (typeof option === 'string' ? option : String(option.value ?? option.name)))
  );
  if (knownOptionValues.has(trimmedValue)) {
    return [trimmedValue];
  }

  const splitByComma = trimmedValue
    .split(',')
    .map((option) => option.trim())
    .filter((option) => option.length > 0);
  if (splitByComma.every((option) => knownOptionValues.has(option))) {
    return splitByComma;
  }

  return [trimmedValue];
}

function TypedValueUnderlyingEditor(
  props: Omit<TypedValueEditorProps, 'label' | 'labelClassName' | 'isRequired' | 'evaluatedCell'>
): JSX.Element {
  const componentId = useRef(uniqueId());
  const clearQuery = (): void => {
    props.onUpdateQuery('');
  };

  function renderStatic(): JSX.Element {
    const cell: Cell = {
      text: formatTypedValue(props.typedValue),
      typedValue: props.typedValue,
    };
    return (
      <TypedValueEditorKeyline locked={true}>
        {getFormattedCellValue(
          props.columnConstraint,
          cell,
          props.typedValue,
          {locked: true},
          props.rowMetadata
        )}
      </TypedValueEditorKeyline>
    );
  }

  if (props.locked) {
    return renderStatic();
  }

  // See the doccomment above: we key off the column type, not the provided typedValue type
  const columnDefinition = props.columnConstraint.columnDefinition!;
  switch (columnDefinition.type) {
    case TableColumnType.MEASUREMENT_AMOUNT: {
      const stringValue = (props.typedValue as StringTypedValue | null)?.rawValue ?? '';
      const isValidNumber =
        isPositiveNumber(stringValue) ||
        (columnDefinition.allowNegative && isNegativeNumber(stringValue)) ||
        (columnDefinition.allowZero && isZero(stringValue));
      return (
        <TextInput
          size={Size.SMALL}
          value={stringValue}
          onChange={(e) => {
            props.onUpdateTypedValue({type: BaseTypedValueType.STRING, rawValue: e.target.value.trim()});
          }}
          hasError={!(columnDefinition.isComputed && stringValue === '') && !isValidNumber}
          placeholder={props.placeholder ?? 'Amount'}
          dataTestAttrs={{component: 'TypedValueEditor', element: 'textInput'}}
        />
      );
    }

    case TableColumnType.UNIT_SELECTOR: {
      const stringValue = (props.typedValue as StringTypedValue | null)?.rawValue ?? '';
      return (
        <TypedValueEditorKeyline>
          <OptionCellDropdown
            staticDisplay={stringValue}
            isSelected={false}
            locked={false}
            options={columnDefinition.acceptedUnits}
            onUpdate={(newUnits) => {
              props.onUpdateTypedValue({type: BaseTypedValueType.STRING, rawValue: newUnits});
            }}
            fullWidth={true}
          />
        </TypedValueEditorKeyline>
      );
    }

    case TableColumnType.OPTION_SEARCH_SELECTOR: {
      if (columnDefinition.schemaField != null && isSchemaFieldDatabaseBacked(columnDefinition.schemaField)) {
        const selectorSchemaField = columnDefinition.schemaField;
        const selectorTagValue: SchemaTag =
          convertTypedValueToTag(props.typedValue, selectorSchemaField) ?? getInitialTag(selectorSchemaField);
        return (
          <TypedValueEditorKeyline>
            <SelectorInput
              autoFocus={false}
              inputValue={props.query ?? ''}
              onBlur={clearQuery}
              onChange={({schemaFieldSelectorOptionIds, value}) => {
                const updatedTagValue = getInitialTag(selectorSchemaField);
                updatedTagValue.schemaFieldSelectorOptionIds = schemaFieldSelectorOptionIds;
                updatedTagValue.value = value;
                props.onUpdateTypedValue(convertTagToTypedValue(updatedTagValue));
              }}
              onKeyDown={noop}
              onUpdateInputValue={props.onUpdateQuery}
              tagValue={selectorTagValue}
              isOutsideTable={true}
            />
          </TypedValueEditorKeyline>
        );
      }

      if (columnDefinition.schemaFieldSelectorId != null) {
        const placeholderSchemaField: SchemaField = {
          id: 0,
          dbId: 0,
          apiId: `pending:${columnDefinition.schemaFieldSelectorId}`,
          name: '',
          displayName: '',
          systemName: '',
          fieldType: FieldType.SELECTOR,
          isRequired: false,
          isMulti: columnDefinition.isMulti ?? false,
          isComputed: false,
          isConvertedFromLink: false,
          isSnapshot: false,
          isIntegration: false,
          isParentLink: false,
          requiredLink: null,
          schemaFieldSelectorId: columnDefinition.schemaFieldSelectorId,
          archiveRecord: null,
        };
        const selectorTagValue: SchemaTag =
          convertTypedValueToTag(props.typedValue, placeholderSchemaField) ??
          getInitialTag(placeholderSchemaField);
        return (
          <TypedValueEditorKeyline>
            <SelectorInput
              autoFocus={false}
              inputValue={props.query ?? ''}
              onBlur={clearQuery}
              onChange={({schemaFieldSelectorOptionIds, value}) => {
                const updatedTagValue = getInitialTag(placeholderSchemaField);
                updatedTagValue.schemaFieldSelectorOptionIds = schemaFieldSelectorOptionIds;
                updatedTagValue.value = value;
                props.onUpdateTypedValue(convertTagToTypedValue(updatedTagValue));
              }}
              onKeyDown={noop}
              onUpdateInputValue={props.onUpdateQuery}
              tagValue={selectorTagValue}
              isOutsideTable={true}
            />
          </TypedValueEditorKeyline>
        );
      }

      const selectedOptions =
        props.typedValue.type === BaseTypedValueType.ARRAY
          ? props.typedValue.rawValue.map((value) => formatTypedValue(value))
          : props.typedValue.type === FieldType.SELECTOR
            ? splitLegacyJoinedSelectorOptions(props.typedValue.value ?? '', columnDefinition.options)
            : (() => {
                const formattedValue = formatTypedValue(props.typedValue);
                return formattedValue === '' ? [] : [formattedValue];
              })();

      if (columnDefinition.isMulti) {
        return (
          <TypedValueEditorKeyline>
            <MultiSelectOptionSearchCellDropdown
              options={columnDefinition.options}
              selectedOptions={selectedOptions}
              isSelected={false}
              onUpdate={(newSelectedOptions) => {
                props.onUpdateTypedValue({
                  type: BaseTypedValueType.ARRAY,
                  rawValue: newSelectedOptions.map((option) => ({
                    type: BaseTypedValueType.STRING,
                    rawValue: option,
                  })),
                });
              }}
            />
          </TypedValueEditorKeyline>
        );
      }

      return (
        <TypedValueEditorKeyline>
          <OptionSearchCellDropdown
            staticDisplay={selectedOptions[0] ?? ''}
            isSelected={false}
            options={columnDefinition.options}
            emptyOptionsDisplay={columnDefinition.emptyOptionsDisplay}
            onUpdate={(newSelection) => {
              props.onUpdateTypedValue({
                type: BaseTypedValueType.STRING,
                rawValue: newSelection,
              });
            }}
            fullWidth={true}
          />
        </TypedValueEditorKeyline>
      );
    }

    case TableColumnType.EXISTING_BIOENTITY: {
      // TODO(BNCH-41594) Handle array typed values
      const bioentitySummary = getBioentitySummaryFromTypedValue(props.typedValue);
      const tagSchemaOrInterface = columnDefinition.tagSchemaOrInterface;
      // If the tagSchema is 'ALL' we want to search across all schemas so we don't filter to a single id
      const tagSchemaOrInterfaceId = tagSchemaOrInterface === 'ALL' ? null : tagSchemaOrInterface?.id;
      return (
        <BioentitySearchInput
          initialQuery={props.query}
          tagSchemaOrInterfaceId={tagSchemaOrInterfaceId}
          onChange={(bioentitySummaries) => {
            const typedValue = getTypedValueFromBioentitySummary(bioentitySummaries[0]) ?? {
              type: BaseTypedValueType.BIOENTITY,
              bioentitySummary: {id: '', name: ''},
            };
            props.onUpdateTypedValue(typedValue);
          }}
          onBlur={clearQuery}
          selectedItems={bioentitySummary ? [bioentitySummary] : []}
          autoFocus={false}
        />
      );
    }

    case TableColumnType.BIOENTITY_SELECTOR: {
      assert(
        columnDefinition.schemaFieldForFiltering != null,
        'Unexpected to find a BIOENTITY_SELECTOR with no filter'
      );
      return (
        <TagValueTypedValueInput
          cell={{text: '', typedValue: props.typedValue}}
          onEscape={() => {
            clearQuery();
            props.onUpdateTypedValue(props.initialTypedValue);
          }}
          onSetCellData={(value: Optional<string>, cell: {typedValue: TypedValue | null}) => {
            clearQuery();
            if (cell.typedValue != null) {
              props.onUpdateTypedValue(cell.typedValue);
            }
          }}
          onBlur={clearQuery}
          query={props.query}
          initialQuery={props.initialQuery}
          onUpdateQuery={props.onUpdateQuery}
          onUpdateTypedValue={props.onUpdateTypedValue}
          schemaField={columnDefinition.schemaFieldForFiltering}
          autoFocus={false}
          isOutsideTable={true}
          areChipsClickable={true}
        />
      );
    }

    case TableColumnType.PROCESS_TASK_ASSIGNEE:
      return (
        <ProcessTaskAssigneeSearchInput
          value={props.typedValue}
          onChange={(typedValue) => {
            props.onUpdateTypedValue(typedValue);
          }}
        />
      );

    case TableColumnType.PROCESS_TASK_STATUS:
      return (
        <ProcessTaskStatusSelector
          lastSyncedUpstreamValue={props.typedValue}
          typedValue={props.typedValue}
          statusLifecycle={columnDefinition.statusLifecycle}
          onUpdate={(typedValue) => {
            props.onUpdateTypedValue(typedValue);
          }}
        />
      );

    case TableColumnType.PROCEDURE: {
      return (
        <ProcedureSearchInput
          query={props.query ?? ''}
          procedureTypes={columnDefinition.procedureTypes}
          placeholder={columnDefinition.placeholder}
          autoFocus={false}
          onUpdateQuery={props.onUpdateQuery}
          onBlur={clearQuery}
          onChange={(newProcedures) => {
            props.onUpdateTypedValue({
              type: BaseTypedValueType.PROCEDURE,
              procedures: newProcedures.map((procedure) => ({
                id: procedure.id,
                name: procedure.name,
              })),
            });
          }}
          shouldAllowMultiple={columnDefinition.shouldAllowMultiple ?? false}
          selectedItems={toProcedureSearchItemsForCell(props.typedValue, props.rowMetadata, columnDefinition)}
        />
      );
    }

    case TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE:
      return (
        <ProcedureSchemaFieldTypeSelectorForTables
          typedValue={props.typedValue}
          onUpdate={props.onUpdateTypedValue}
          context={columnDefinition.context}
        />
      );

    case TableColumnType.SAMPLE_RESTRICTION_STATUS:
      return (
        <SampleRestrictionStatusTypedValueSelector
          typedValue={props.typedValue}
          onUpdate={props.onUpdateTypedValue}
        />
      );

    // Position columns cannot be parsed without knowing the size of the plate. See
    // DestinationPositionResolver for the code. In the absence of better context, we
    // use a string editor for position columns, and let the parent handle evaluation.
    case TableColumnType.POSITION:
    case TableColumnType.INGREDIENT_CATALOG_ID:
    case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
    case TableColumnType.INGREDIENT_NOTES:
    case TableColumnType.STRING:
    case TableColumnType.BIOENTITY_NAME:
    case TableColumnType.PROCESS_TASK:
    case TableColumnType.PLATE:
    case TableColumnType.BOX:
    case TableColumnType.DNA_BASES:
    case TableColumnType.RNA_BASES:
    case TableColumnType.DNA_HELM:
    case TableColumnType.RNA_HELM:
    case TableColumnType.DNA_IDT:
    case TableColumnType.RNA_IDT:
    case TableColumnType.SMILES: {
      const stringValue = (props.typedValue as StringTypedValue | null)?.rawValue ?? '';
      return (
        <FormInput
          autoFocus={false}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            props.onUpdateTypedValue({type: BaseTypedValueType.STRING, rawValue: e.target.value});
          }}
          placeholder={props.placeholder ?? 'Value'}
          value={stringValue}
          dataTestAttrs={{component: 'TypedValueEditor', element: 'formInput'}}
        />
      );
    }

    case TableColumnType.PARTIES: {
      return (
        <SampleRolesPartiesSearchInput
          typedValue={props.typedValue}
          handleUpdateTypedValue={props.onUpdateTypedValue}
          onSetCellData={(value, cell) => {
            if (cell.typedValue != null) {
              props.onUpdateTypedValue(cell.typedValue);
            }
          }}
          onEscape={noop}
          onTabKey={noop}
          allowBenchlingApps={columnDefinition.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS}
        />
      );
    }

    case TableColumnType.NUMBER_OF_CONTAINERS: {
      const numericValue =
        props.typedValue?.type === BaseTypedValueType.NUMBER ? props.typedValue.rawValue : 0;
      return (
        <NumericInput
          focusOnMount={false}
          onChange={(e: NumericInputChangeEvent) => {
            // Non-numeric values are coerced to a string. Error flags will be shown as needed
            props.onUpdateTypedValue(
              e.target.numericValue != null
                ? {type: BaseTypedValueType.NUMBER, rawValue: e.target.numericValue}
                : {type: BaseTypedValueType.STRING, rawValue: e.target.value}
            );
          }}
          placeholder={props.placeholder ?? 'Number of containers'}
          value={numericValue}
        />
      );
    }

    case TableColumnType.ALIAS:
      /**
       Alias cells are a challenge we aren't taking up here:
       - For a non-finalized row, the user can enter a single string, to add at most a single alias to the entity.
         Punctuation is incorporated into the entity name, not used as a delimiter.
       - If the user has a finalized entity with multiple aliases (which must be set in the registry view), we
         render those aliases as multiple chips
       - Once a row is finalized, no edits are allowed

       For now, we disable editing altogether.
      */
      return renderStatic();

    case TableColumnType.BOOLEAN:
      return (
        <CheckboxInput
          label={props.placeholder ?? ''}
          onChange={(e: InputChangeEvent) => {
            props.onUpdateTypedValue({type: BaseTypedValueType.BOOLEAN, booleanValue: e.target.checked});
          }}
          id={componentId.current}
          checked={
            props.typedValue.type === BaseTypedValueType.BOOLEAN ? props.typedValue.booleanValue : false
          }
        />
      );

    case TableColumnType.PARENT_STORABLE: {
      const emptyStorablesValue: TypedValue = {type: BaseTypedValueType.STORABLES, storableSummaries: []};
      return (
        <TypedValueEditorKeyline>
          <LocationSelectorInput
            autoFocus={false}
            onSetData={(value, typedValue) => {
              props.onUpdateQuery('');
              props.onUpdateTypedValue(typedValue ?? emptyStorablesValue);
            }}
            onUpdateTypedValue={(tv) => {
              props.onUpdateTypedValue(tv ?? emptyStorablesValue);
            }}
            onUpdateQuery={props.onUpdateQuery}
            query={props.query ?? ''}
            typedValue={props.typedValue}
            registryId={
              columnDefinition.storableSchema?.registryId ?? columnDefinition.fallbackRegistryId ?? ''
            }
            parentStorableType={columnDefinition.parentStorableType}
            onBlur={clearQuery}
            onEscape={noop}
            onTabKey={noop}
            isOutsideTable={true}
            includeArchived={false}
          />
        </TypedValueEditorKeyline>
      );
    }

    case TableColumnType.PROCEDURE_CONDITION_REPLICATES:
      return (
        <TypedValueEditorKeyline>
          <EditorPropertiesContext.Consumer>
            {({fileId}) => (
              <ProcedureConditionReplicateCellEditor
                fileId={fileId ?? ''}
                onUpdate={(typedValue) => {
                  if (typedValue != null) {
                    props.onUpdateTypedValue(typedValue);
                  }
                }}
                typedValue={
                  props.typedValue?.type === BaseTypedValueType.PROCEDURE_CONDITION_REPLICATES
                    ? props.typedValue
                    : null
                }
                isOutsideTable={true}
              />
            )}
          </EditorPropertiesContext.Consumer>
        </TypedValueEditorKeyline>
      );

    case TableColumnType.STUDIES: {
      return (
        <StudySearchInput
          query={props.query ?? ''}
          autoFocus={false}
          onUpdateQuery={props.onUpdateQuery}
          onBlur={clearQuery}
          onChange={(newStudies) => {
            props.onUpdateTypedValue({
              type: BaseTypedValueType.STUDIES,
              studies: newStudies.map((study) => ({
                id: study.id,
                humanId: study.humanId,
                name: study.name,
              })),
            });
          }}
          shouldAllowMultiple={true}
          selectedItems={props.typedValue.type === BaseTypedValueType.STUDIES ? props.typedValue.studies : []}
        />
      );
    }

    /**
     * Tag-value types
     */
    case TableColumnType.TAG_VALUE: {
      const {
        name,
        isRequired,
        schemaField,
        tagSchemaFieldType,
        tagSchemaFieldDisplayName,
        tagSchemaFieldIsComputed,
        tagSchemaFieldIsConvertedFromLink,
      } = columnDefinition;

      // Overload the SchemaField with the versioned values from the column
      const versionedSchemaField = {
        ...schemaField,
        fieldType: tagSchemaFieldType,
        displayName: tagSchemaFieldDisplayName,
        isComputed: tagSchemaFieldIsComputed,
        isConvertedFromLink: tagSchemaFieldIsConvertedFromLink,
        isRequired,
        name: name ?? schemaField.name,
      };
      // Note: the converted tag may be null. This is most likely to happen before the user starts editing,
      // and most likely with string columns with mismatched types. (E.g., the schema field is FieldType.TEXT,
      // but the initial typed value is STRING.) This is not a bug; just use a default value when needed.
      const tag: SchemaTag | null = convertTypedValueToTag(props.typedValue, versionedSchemaField);

      // Dropdown/search components (e.g., for bioentities or selectors) are provided here
      if (INLINE_TAG_TYPES.includes(tagSchemaFieldType)) {
        const input = (
          <TagValueTypedValueInput
            cell={{text: '', typedValue: props.typedValue}}
            onEscape={() => {
              clearQuery();
              props.onUpdateTypedValue(props.initialTypedValue);
            }}
            onSetCellData={(value: Optional<string>, cell: {typedValue: TypedValue | null}) => {
              clearQuery();
              if (cell.typedValue != null) {
                props.onUpdateTypedValue(cell.typedValue);
              }
            }}
            onBlur={clearQuery}
            query={props.query}
            initialQuery={props.initialQuery}
            onUpdateQuery={props.onUpdateQuery}
            onUpdateTypedValue={props.onUpdateTypedValue}
            schemaField={versionedSchemaField}
            autoFocus={false}
            isOutsideTable={true}
            areChipsClickable={true}
          />
        );
        // The Selector input needs a keyline
        return tagSchemaFieldType === FIELD_TYPES.SELECTOR.type ? (
          <TypedValueEditorKeyline>{input}</TypedValueEditorKeyline>
        ) : (
          input
        );
      }

      switch (columnDefinition.tagSchemaFieldType) {
        case FieldType.BLOB_LINK: {
          const defaultValue: SchemaTag = tag ?? {
            targetFiles: [],
            value: null,
            tagSchemaField: versionedSchemaField,
            name: '',
          };
          return (
            <BlobLinkInput
              defaultValue={defaultValue}
              onChange={({targetBlobs, value}) => {
                props.onUpdateTypedValue({
                  type: FieldType.BLOB_LINK,
                  value,
                  targetBlobs: sanitizeBenchlingBlobs(targetBlobs),
                });
              }}
            />
          );
        }
        case FieldType.TEXT:
          return (
            <TextAndUrlValueEditor
              autoFocus={false}
              defaultValue={
                tag ?? {
                  value: '',
                  tagSchemaField: versionedSchemaField,
                  name: '',
                }
              }
              itemLinkAllowed={false}
              onChange={(e: {value: string}) => {
                props.onUpdateTypedValue({type: FieldType.TEXT, value: e.value});
              }}
              placeholder={props.placeholder ?? 'Value'}
            />
          );

        case FieldType.LONG_TEXT:
          return (
            <LongTextValueEditor
              defaultValue={
                tag ?? {
                  value: '',
                  tagSchemaField: versionedSchemaField,
                  name: '',
                }
              }
              autoFocus={false}
              onChange={(e: {value: string}) => {
                props.onUpdateTypedValue({type: FieldType.LONG_TEXT, value: e.value});
              }}
              placeholder={props.placeholder ?? 'Value'}
            />
          );

        case FieldType.INTEGER:
          return (
            <ControlledIntegerValueEditor
              autoFocus={false}
              defaultValue={tag}
              // While the contract indicates we should get a numeric `integerValue`, in practice,
              // it may be a string (e.g., if the user enters non-numeric characters)
              onChange={(e: {value: string | null; integerValue: number | string}) => {
                let tv: TypedValue | null = null;
                const int =
                  typeof e.integerValue === 'string' ? parseInteger(e.integerValue) : e.integerValue;
                if (typeof e.integerValue === 'string' && int == null) {
                  // This is a string that doesn't parse to a number. We use a string typedValue to trigger error checking.
                  tv = {
                    type: BaseTypedValueType.STRING,
                    rawValue: e.integerValue,
                  };
                }
                props.onUpdateTypedValue(
                  tv || {
                    type: FieldType.INTEGER,
                    value: e.value,
                    integerValue: int,
                  }
                );
              }}
              value={
                (props.typedValue as IntegerSchemaFieldTypedValue).integerValue ??
                formatTypedValue(props.typedValue)
              }
              placeholder={props.placeholder ?? 'Value'}
            />
          );

        case FieldType.BOOLEAN:
          return (
            <BooleanValueEditor
              defaultValue={tag?.booleanValue}
              onUpdate={(tag: {value: string; booleanValue: boolean | null}) => {
                props.onUpdateTypedValue({
                  type: FieldType.BOOLEAN,
                  value: tag.value,
                  booleanValue: tag.booleanValue,
                });
              }}
              placeholder={props.placeholder ?? 'Value'}
            />
          );

        case FieldType.FLOAT:
          return (
            <ControlledFloatValueEditor
              autoFocus={false}
              defaultValue={tag}
              onChange={(e: {value: string | null; floatValue: number | string}) => {
                let tv: TypedValue | null = null;
                const float =
                  typeof e.floatValue === 'string' ? parseTextAsFloat(e.floatValue) : e.floatValue;
                if (typeof e.floatValue === 'string' && float == null) {
                  // This is a string that doesn't parse to a number. We use a string typedValue to trigger error checking.
                  tv = {
                    type: BaseTypedValueType.STRING,
                    rawValue: e.floatValue,
                  };
                }
                props.onUpdateTypedValue(
                  tv || {
                    type: FieldType.FLOAT,
                    value: e.value,
                    rawValue: e.value,
                    floatValue: float,
                  }
                );
              }}
              value={
                (props.typedValue as FloatSchemaFieldTypedValue).floatValue ??
                formatTypedValue(props.typedValue)
              }
              placeholder={props.placeholder ?? 'Value'}
            />
          );
        case FieldType.JSON:
          return renderStatic();
        default:
          assert(false, 'TypedValue editor invoked for a schema field type that is not supported yet', {
            typedValue: props.typedValue,
            columnConstraint: props.columnConstraint,
          });
      }
      break;
    }

    case TableColumnType.ANTIBODY_DOMAIN: {
      const bioentitySummary = getBioentitySummaryFromTypedValue(props.typedValue);
      // TODO(BNCH-128762) Use the specified schema from the frontend metadata to limit to that schema.
      return (
        <BioentitySearchInput
          initialQuery={props.query}
          bioentityTypes={[ModelTypeConstants.PROTEIN]}
          onChange={(bioentitySummaries) => {
            const typedValue = getTypedValueFromBioentitySummary(bioentitySummaries[0]) ?? {
              type: BaseTypedValueType.BIOENTITY,
              bioentitySummary: {id: '', name: ''},
            };
            props.onUpdateTypedValue(typedValue);
          }}
          onBlur={clearQuery}
          selectedItems={bioentitySummary ? [bioentitySummary] : []}
          autoFocus={false}
        />
      );
    }

    default:
      assert(
        false,
        `TypedValue editor invoked for a type that is not supported yet: ${columnDefinition.type}`,
        {typedValue: props.typedValue}
      );
  }
}

const typedValueEditorStyles = StyleSheet.create({
  // The label control is styled with "font-weight: bold". Our usage of it includes
  // embedding the form control inside: <label>Text {control}</label>
  // Unfortunately, the control then inherits the bold style. Here we undo that.
  labelDescendants: {
    display: 'block',
    marginTop: '5px',
    fontWeight: 'normal',
  },
  triangleWrapper: {
    width: '100%',
    position: 'relative',
  },
  lockedEditorWrapper: {
    background: `${GRAY_LIGHTER}`,
  },
});
